import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { shopierService } from '../services/shopier.service';
import { getPlanFeatures, PlanFeatureItem } from '../utils/planFeatures';
import '../styles/pricing.css';

// Ödeme sistemi entegre edilene kadar plan tipi burada tanımlanır
interface Plan {
  id: string;
  name: string;
  tier: string;
  price_try: number;
  type: string;
  description?: string;
  badge_label?: string;
  features?: string[];
  limits?: { daily_ai_messages?: number | null };
}


const DEFAULT_PLANS: Partial<Plan>[] = [
  {
    id: 'free-plan',
    name: 'Ücretsiz',
    tier: 'free',
    price_try: 0,
    type: 'individual',
    description: 'Denemeye başlamak için',
    features: ['course.access', 'lesson.preview', 'ai.chat'],
    limits: { daily_ai_messages: 10 },
  },
  {
    id: 'premium-plan',
    name: 'Premium',
    tier: 'premium',
    price_try: 799,
    type: 'individual',
    description: 'Tam kapasite yapay zeka desteği',
    badge_label: 'En Çok Tercih Edilen',
    features: [
      'course.access',
      'course.premium',
      'lesson.premium',
      'ai.mentor',
      'ai.code_review',
      'ai.code_fix',
      'ai.project_generator',
      'certificate.generate',
      'parent.report',
    ],
    limits: { daily_ai_messages: null },
  },
  {
    id: 'pro-plan',
    name: 'Pro',
    tier: 'pro',
    price_try: 1299,
    type: 'individual',
    description: 'Birebir YZ rehberliği ve portfolyo',
    badge_label: 'Gelişmiş Seçenek',
    features: [
      'course.access',
      'course.premium',
      'lesson.premium',
      'ai.mentor',
      'ai.code_review',
      'ai.code_fix',
      'ai.project_generator',
      'certificate.generate',
      'parent.report',
      'hackathon.join',
    ],
    limits: { daily_ai_messages: null },
  },
  {
    id: 'school-plan',
    name: 'Okullar İçin',
    tier: 'school',
    price_try: 0,
    type: 'school',
    description: 'Sınıf ve kurum yönetimi',
    features: [
      'school.dashboard',
      'teacher.panel',
      'student.management',
      'report.pdf',
      'report.excel',
    ],
    limits: { daily_ai_messages: null },
  },
];

const FEATURE_DISPLAY: Record<string, string> = {
  // Noktalı
  'course.access':        'Temel Ders İçerikleri',
  'course.premium':       'Tüm Premium Dersler',
  'lesson.preview':       'Ders Önizleme Hakları',
  'lesson.premium':       'Sınırsız Tüm Dersler',
  'ai.chat':              'Günde 10 YZ Mesajı',
  'ai.mentor':            'Sınırsız YZ Mentorluk',
  'ai.code_review':       'YZ Kod İncelemesi',
  'ai.code_fix':          'YZ Otomatik Kod Düzeltme',
  'ai.project_generator': 'YZ Proje Üretici',
  'ai.quiz_generator':    'YZ Sınav/Quiz Üretici',
  'ai.learning_plan':     'YZ Özel Öğrenme Planı',
  'certificate.generate': 'Doğrulanmış Sertifikalar',
  'hackathon.join':       'Hackathon Katılımı',
  'parent.report':        'Haftalık YZ Veli Raporu',
  'teacher.panel':        'Sınıf ve Öğretmen Paneli',
  'school.dashboard':     'Kurumsal Okul Paneli',
  'student.management':   'Gelişmiş Öğrenci Analitiği',
  'report.pdf':           'Detaylı PDF Raporları',
  'report.excel':         'Detaylı Excel Raporları',
  'api.access':           'API Erişimi',
  'adventure.mode':       'Macera Modu',
  'challenge.premium':    'Premium Görevler',

  // Boşluklu
  'course access':        'Temel Ders İçerikleri',
  'course premium':       'Tüm Premium Dersler',
  'lesson preview':       'Ders Önizleme Hakları',
  'lesson premium':       'Sınırsız Tüm Dersler',
  'ai chat':              'Günde 10 YZ Mesajı',
  'ai mentor':            'Sınırsız YZ Mentorluk',
  'ai code review':       'YZ Kod İncelemesi',
  'ai code fix':          'YZ Otomatik Kod Düzeltme',
  'ai project generator': 'YZ Proje Üretici',
  'ai quiz generator':    'YZ Sınav/Quiz Üretici',
  'ai learning plan':     'YZ Özel Öğrenme Planı',
  'certificate generate': 'Doğrulanmış Sertifikalar',
  'hackathon join':       'Hackathon Katılımı',
  'parent report':        'Haftalık YZ Veli Raporu',
  'teacher panel':        'Sınıf ve Öğretmen Paneli',
  'school dashboard':     'Kurumsal Okul Paneli',
  'student management':   'Gelişmiş Öğrenci Analitiği',
  'report pdf':           'Detaylı PDF Raporları',
  'report excel':         'Detaylı Excel Raporları',
  'api access':           'API Erişimi',
  'adventure mode':       'Macera Modu',
  'challenge premium':    'Premium Görevler',

  // Alt çizgili
  'course_access':        'Temel Ders İçerikleri',
  'course_premium':       'Tüm Premium Dersler',
  'lesson_preview':       'Ders Önizleme Hakları',
  'lesson_premium':       'Sınırsız Tüm Dersler',
  'ai_chat':              'Günde 10 YZ Mesajı',
  'ai_mentor':            'Sınırsız YZ Mentorluk',
  'ai_code_review':       'YZ Kod İncelemesi',
  'ai_code_fix':          'YZ Otomatik Kod Düzeltme',
  'ai_project_generator': 'YZ Proje Üretici',
  'ai_quiz_generator':    'YZ Sınav/Quiz Üretici',
  'ai_learning_plan':     'YZ Özel Öğrenme Planı',
  'certificate_generate': 'Doğrulanmış Sertifikalar',
  'hackathon_join':       'Hackathon Katılımı',
  'parent_report':        'Haftalık YZ Veli Raporu',
  'teacher_panel':        'Sınıf ve Öğretmen Paneli',
  'school_dashboard':     'Kurumsal Okul Paneli',
  'student_management':   'Gelişmiş Öğrenci Analitiği',
  'report_pdf':           'Detaylı PDF Raporları',
  'report_excel':         'Detaylı Excel Raporları',
  'api_access':           'API Erişimi',
  'adventure_mode':       'Macera Modu',
  'challenge_premium':    'Premium Görevler',
};

function formatFeatureName(key: string): string {
  if (FEATURE_DISPLAY[key]) return FEATURE_DISPLAY[key];
  
  // Normalizasyon
  const normalized = key.toLowerCase().replace(/[._]/g, ' ').trim();
  if (FEATURE_DISPLAY[normalized]) return FEATURE_DISPLAY[normalized];

  return normalized
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function PricingPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isYearly, setIsYearly] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [expandedPlans, setExpandedPlans] = useState<Record<string, boolean>>({});
  // Fake Door MVP: Toast state
  const [toast, setToast] = useState<{ message: string; visible: boolean }>({ message: '', visible: false });

  function togglePlanFeatures(planId: string) {
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  }

  function showToast(message: string) {
    setToast({ message, visible: true });
    setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 6000);
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const paymentStatus = searchParams.get('paymentStatus');
    const reason = searchParams.get('reason');

    if (paymentStatus === 'success') {
      setMsg({
        text: '🎉 Ödeme İşleminiz Başarıyla Tamamlandı! Premium Aboneliğiniz ve Yapay Zeka Ayrıcalıklarınız Aktif Edildi. E-posta adresinize onay maili gönderilmiştir.',
        ok: true,
      });
    } else if (paymentStatus === 'failed') {
      setMsg({
        text: `❌ Ödeme İşlemi Tamamlanamadı: ${reason || 'Lütfen kart bilgilerinizi kontrol edip tekrar deneyiniz.'}`,
        ok: false,
      });
    }
  }, []);

  useEffect(() => {
    supabase
      .from('plans')
      .select('*')
      .then(({ data, error }) => {
        if (!error && data && data.length > 0) {
          const activePlans = data.filter((p: any) => p.is_active !== false);
          setPlans(activePlans.length > 0 ? (activePlans as Plan[]) : (DEFAULT_PLANS as Plan[]));
        } else {
          setPlans(DEFAULT_PLANS as Plan[]);
        }
      })
      .catch(() => {
        setPlans(DEFAULT_PLANS as Plan[]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  async function handleSelect(plan: Plan) {
    if (plan.tier === 'free') {
      window.location.href = '/dashboard';
      return;
    }
    if (plan.tier === 'school') {
      setMsg({ text: 'Okul yetkili kayıtları ve kurumsal lisans teklifleri için info@minikcoderakademi.com adresinden iletişime geçebilirsiniz.', ok: true });
      return;
    }

    setIsCheckoutLoading(true);
    setMsg(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setMsg({ text: 'Plan seçimi ve ödeme için lütfen önce oturum açınız.', ok: false });
        setIsCheckoutLoading(false);
        setTimeout(() => {
          window.location.href = '/login?redirect=/pricing';
        }, 1500);
        return;
      }

      // Shopier Ödeme Akışını Başlat (Doğrudan Ürün Linkine Yönlendir)
      await shopierService.startPayment(plan.id, plan.name, isYearly ? 'yearly' : 'monthly');
    } catch (err: any) {
      console.error('[Pricing] Shopier ödeme başlatma hatası:', err);
      setMsg({ text: err?.message || 'Ödeme sistemi başlatılamadı. Lütfen tekrar deneyiniz.', ok: false });
      setIsCheckoutLoading(false);
    }
  }

  if (isLoading) return <div className="dash-loading">Planlar yükleniyor...</div>;

  const displayPlans = plans.length > 0 ? plans : (DEFAULT_PLANS as Plan[]);

  return (
    <div className="pricing-v2-page">
      <div className="pricing-v2-header">
        <h1>Ailene Uygun Bir Plan 💎</h1>
        <p>İhtiyacına uygun planı seç, yapay zeka destekli kodlama yolculuğuna başla.</p>

        {/* Aylık / Yıllık Seçim Toggle'ı */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '24px' }}>
          <button
            type="button"
            onClick={() => setIsYearly(false)}
            style={{
              padding: '8px 18px',
              borderRadius: '20px',
              border: !isYearly ? '2px solid #17c79a' : '1px solid rgba(255,255,255,0.1)',
              background: !isYearly ? 'rgba(23,199,154,0.15)' : 'transparent',
              color: !isYearly ? '#17c79a' : '#94a3b8',
              fontWeight: !isYearly ? '700' : '500',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            Aylık Ödeme
          </button>
          <button
            type="button"
            onClick={() => setIsYearly(true)}
            style={{
              padding: '8px 18px',
              borderRadius: '20px',
              border: isYearly ? '2px solid #06b6d4' : '1px solid rgba(255,255,255,0.1)',
              background: isYearly ? 'rgba(6,182,212,0.15)' : 'transparent',
              color: isYearly ? '#06b6d4' : '#94a3b8',
              fontWeight: isYearly ? '700' : '500',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span>Yıllık Ödeme</span>
            <span style={{ background: '#f59e0b', color: '#000', fontSize: '11px', fontWeight: '800', padding: '2px 6px', borderRadius: '8px' }}>%20 İndirim</span>
          </button>
        </div>
      </div>

      {msg && (
        <div className="pricing-msg" style={{
          color: msg.ok ? 'var(--teal-deep)' : 'var(--crimson)',
          borderColor: msg.ok ? 'rgba(23,199,154,0.4)' : 'rgba(172,59,48,0.4)',
          background: msg.ok ? 'rgba(23,199,154,0.1)' : 'rgba(172,59,48,0.1)',
        }}>
          {msg.text}
        </div>
      )}

      <div className="pricing-v2-grid">
        {displayPlans.map((plan) => {
          const isFeatured = plan.tier === 'premium' || (plan as any).badge_label?.includes('Popüler') || (plan as any).badge_label?.includes('Tercih');
          const badgeText = (plan as any).badge_label || (isFeatured ? 'En Çok Tercih Edilen' : (plan.tier === 'free' ? 'Başlangıç' : ''));
          const allFeatures = getPlanFeatures(plan);
          const INITIAL_COUNT = 5;
          const isExpanded = !!expandedPlans[plan.id as string];
          const visibleFeatures = isExpanded ? allFeatures : allFeatures.slice(0, INITIAL_COUNT);
          const remainingCount = allFeatures.length - INITIAL_COUNT;

          const isSchool = plan.tier === 'school' || (plan as any).type === 'school' || plan.tier?.toLowerCase().includes('school') || plan.tier?.toLowerCase().includes('okul') || plan.name?.toLowerCase().includes('okul');

          return (
            <div key={plan.id} className={`pricing-v2-card ${isFeatured ? 'featured' : ''}`}>
              {badgeText && <div className="pricing-v2-badge">{badgeText}</div>}
              
              <div className="pricing-v2-top">
                <div className="pricing-v2-name">{plan.name}</div>
                <p className="plan-desc">{(plan as any).description || 'MinikCoder eğitim ve yapay zeka paketi'}</p>
                
                <div className="pricing-v2-price">
                  {isSchool ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span className="pricing-v2-amount" style={{ fontSize: '1.85rem', fontWeight: 800, color: 'var(--primary-light, #6366F1)' }}>
                        Özel Teklif
                      </span>
                      <span className="pricing-v2-period" style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                        Kurumsal Anlaşmalı / Lisans
                      </span>
                    </div>
                  ) : (
                    <>
                      <span className="pricing-v2-amount">
                        {plan.price_try === 0 ? '₺0' : `₺${isYearly ? Math.round(plan.price_try * 0.8) : plan.price_try}`}
                      </span>
                      {plan.price_try > 0 && (
                        <span className="pricing-v2-period">
                          {isYearly ? '/ay (yıllık faturalandırılır)' : '/ay'}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="pricing-v2-features-wrap">
                <ul className="pricing-v2-features">
                  {visibleFeatures.map((item) => (
                    <li key={item.id} className="included">
                      <Check size={16} className="pricing-check-icon" style={{ marginTop: 2, flexShrink: 0 }} />
                      <div className="feat-content">
                        <span className="feat-title">{item.title}</span>
                        {item.desc && <span className="feat-desc">{item.desc}</span>}
                      </div>
                    </li>
                  ))}
                </ul>

                {allFeatures.length > INITIAL_COUNT && (
                  <button
                    type="button"
                    className={`pricing-expand-btn ${isExpanded ? 'active' : ''}`}
                    onClick={() => togglePlanFeatures(plan.id as string)}
                  >
                    <span>{isExpanded ? 'Daha Az Özellik Göster' : `Tüm Özellikleri Gör (${remainingCount}+)`}</span>
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                )}
              </div>

              <div className="pricing-v2-actions">
                {plan.tier === 'free' || (!isSchool && plan.price_try === 0) ? (
                  <Link to="/auth?mode=register-student" className="pricing-v2-btn plan-btn-outline">
                    Ücretsiz Başla
                  </Link>
                ) : isSchool ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                    <Link
                      to="/#iletisim"
                      className="pricing-v2-btn plan-btn-primary"
                      style={{ textAlign: 'center', background: 'linear-gradient(135deg, #0EA5E9, #2563EB)', color: '#fff', border: 'none' }}
                    >
                      Kurumsal İletişim & Teklif Al
                    </Link>
                    <Link
                      to="/auth?mode=school"
                      className="pricing-v2-btn plan-btn-outline"
                      style={{ textAlign: 'center', fontSize: '0.82rem', padding: '6px 12px' }}
                    >
                      Okul Paneli Girişi
                    </Link>
                  </div>
                ) : (
                  <button
                    className="pricing-v2-btn plan-btn-primary"
                    disabled={isCheckoutLoading}
                    onClick={() => handleSelect(plan)}
                  >
                    {isCheckoutLoading ? 'Yükleniyor...' : `${plan.name}'a Geç`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Fake Door MVP: Toast Bildirimi */}
      <div className={`pricing-toast ${toast.visible ? 'pricing-toast-visible' : ''}`}>
        <div className="pricing-toast-icon">🔧</div>
        <div className="pricing-toast-content">
          <strong>Planlı Güncelleme</strong>
          <p>{toast.message}</p>
        </div>
        <button
          type="button"
          className="pricing-toast-close"
          onClick={() => setToast((prev) => ({ ...prev, visible: false }))}
        >
          ✕
        </button>
      </div>
    </div>
  );
}